import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react'

type ToastTone = 'success' | 'info' | 'error'

interface ToastMessage {
  id: number
  message: string
  tone: ToastTone
}

interface ToastOptions {
  message: string
  tone?: ToastTone
  durationMs?: number
}

interface ToastContextValue {
  toasts: ToastMessage[]
  showToast: (options: ToastOptions) => void
  dismissToast: (id: number) => void
}

const ToastContext = createContext<ToastContextValue | null>(null)
const DEFAULT_DURATION_MS = 3600

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastMessage[]>([])
  const nextIdRef = useRef(1)

  const dismissToast = useCallback((id: number) => {
    setToasts((current) => current.filter((toast) => toast.id !== id))
  }, [])

  const showToast = useCallback(
    ({ message, tone = 'info', durationMs = DEFAULT_DURATION_MS }: ToastOptions) => {
      const id = nextIdRef.current++
      setToasts((current) => [...current, { id, message, tone }])
      window.setTimeout(() => dismissToast(id), durationMs)
    },
    [dismissToast],
  )

  const value = useMemo(
    () => ({ toasts, showToast, dismissToast }),
    [dismissToast, showToast, toasts],
  )

  return <ToastContext.Provider value={value}>{children}</ToastContext.Provider>
}

export function useToast() {
  const context = useContext(ToastContext)
  if (!context) throw new Error('useToast must be used within ToastProvider')
  return { showToast: context.showToast }
}

export function ToastContainer() {
  const context = useContext(ToastContext)
  if (!context || context.toasts.length === 0) return null

  return (
    <div
      className="pointer-events-none fixed right-4 bottom-4 z-50 flex max-w-[calc(100vw-2rem)] flex-col items-end gap-2"
      aria-live="polite"
      aria-atomic="true"
    >
      {context.toasts.map((toast) => (
        <div
          key={toast.id}
          role="status"
          className={`rounded-[var(--xr-radius-lg)] border px-4 py-3 text-sm shadow-[var(--xr-shadow-md)] ${
            toast.tone === 'success'
              ? 'border-[var(--xr-success)] bg-[var(--xr-success-soft)] text-[var(--xr-success)]'
              : toast.tone === 'error'
                ? 'border-[var(--xr-danger)] bg-[var(--xr-danger-soft)] text-[var(--xr-danger)]'
                : 'border-[var(--xr-border-default)] bg-[var(--xr-bg-surface)] text-[var(--xr-text-primary)]'
          }`}
        >
          {toast.message}
        </div>
      ))}
    </div>
  )
}
